import { useMemo, useState } from 'react';
import {
  normalizeFieldDef,
  contrastTextColor,
  type AiOutputFormat,
  type AnalysisAxisRule,
  type AnalysisDisplayRules,
  type FieldDef,
} from '@citizen-walk/shared-types';
import type { AiCategoryMastersResult, PostMinimal } from '../posts';
import {
  buildAxisGroups,
  buildAxisOptions,
  collectCustomAxes,
  collectIncludeEmptyKeys,
  deriveAxisOptionsFromDataSettings,
  postCodesForAxis,
  type AxisGroup,
  type PostFieldAxisGroup,
} from './matrixAxisModel';
import type { AnalysisPerspective } from './analysisPerspectives';

/**
 * 分析ツール共通の「軸駆動フィルタ」フック + パネル。
 *
 * マトリクス / 分析画面 / 地図と同じ matrixAxisModel エンジン (master / dataSettings.postFields /
 * M5 custom 軸 + bucketMethod) で軸候補 + bucket を導出し、選択した bucket で投稿を絞り込む。
 * ヒートマップ / ネットワークなど他の分析ビューでも同じフィルタ UX を再利用するための共有実装
 * (ハードコードを排し、どのモードでも動的に動く)。
 */

const EMPTY_RESOLVER: AiCategoryMastersResult = {
  byCategoryCd: new Map(),
  byCategoryLabel: new Map(),
  byTemplateVar: new Map(),
  masters: [],
  loading: false,
};

export interface AxisFilterDeps {
  /** master 由来軸 (感情 / 対象 / 任意 templateVar)。未指定なら master 軸なし。 */
  resolver?: AiCategoryMastersResult;
  /** dataSettings.postFields 由来軸 (rating-scale / select / multiselect)。 */
  postFields?: FieldDef[];
  /** M5 マトリクスルール (custom 軸 / hiddenAxisKeys / includeEmpty)。 */
  matrixRules?: AnalysisDisplayRules['matrix'];
  /** M3 出力フォーマット (format 駆動の軸 code 解決)。 */
  outputFormat?: AiOutputFormat | null;
  /** docs/29 §8-1: 追加の AI 解析の観点 (軸の候補に観点ごとの AI 解析の軸を足す)。 */
  perspectives?: readonly AnalysisPerspective[];
  /** 既定の観点の表示名 (追加の観点があるとき軸のラベルに付ける)。 */
  defaultPerspectiveLabel?: string | null;
}

export interface AxisFilterResult {
  axisOptions: { key: string; label: string }[];
  filterGroupsByKey: Map<string, AxisGroup[]>;
  filterSelections: Record<string, Set<string>>;
  toggleFilterValue: (axisKey: string, code: string) => void;
  clearAllFilters: () => void;
  activeFilterCount: number;
  /** 与えた posts に現在のフィルタ選択を AND 合成で適用する。 */
  applyFilter: (posts: PostMinimal[]) => PostMinimal[];
  /** sourcePosts に現在のフィルタを適用した安定参照 (memo 化済)。 */
  filteredPosts: PostMinimal[];
  /** 気づきワークスペース等へ渡す可読フィルタ文字列。 */
  buildFilterList: () => string[];
  /**
   * 指定軸での post の代表 bucket 色 (master colorMap / row.color 由来)。
   * ヒートマップ / ネットワークのマーカー色を「色分け軸」で動的に塗るための共有解決。
   * 軸を持たない post / 軸キー空のときは null。
   */
  colorForPost: (post: PostMinimal, axisKey: string) => string | null;
  /** 指定軸の bucket 一覧 (色分け凡例用)。 */
  groupsForAxis: (axisKey: string) => AxisGroup[];
}

/**
 * 軸駆動フィルタの状態 + 適用ロジック。`sourcePosts` は bucket 列挙の母集団
 * (絞込前の全件を渡すと、絞込んでも選択肢が消えない)。
 */
export function useAxisFilter(sourcePosts: PostMinimal[], deps: AxisFilterDeps): AxisFilterResult {
  const { resolver, postFields, matrixRules, outputFormat, perspectives, defaultPerspectiveLabel } =
    deps;
  const safeResolver = resolver ?? EMPTY_RESOLVER;

  const normFields = useMemo(() => (postFields ?? []).map(normalizeFieldDef), [postFields]);
  const customAxes = useMemo(() => collectCustomAxes(matrixRules), [matrixRules]);
  const customRuleByKey = useMemo(() => {
    const m = new Map<string, AnalysisAxisRule>();
    for (const c of customAxes) m.set(c.key, c.rule);
    return m;
  }, [customAxes]);
  const postFieldAxes = useMemo(() => deriveAxisOptionsFromDataSettings(normFields), [normFields]);
  const postFieldAxisByKey = useMemo(() => {
    const m = new Map<string, { field: FieldDef; groups: PostFieldAxisGroup[] }>();
    for (const a of postFieldAxes) m.set(a.key, { field: a.field, groups: a.groups });
    return m;
  }, [postFieldAxes]);
  const includeEmptyKeys = useMemo(() => collectIncludeEmptyKeys(matrixRules), [matrixRules]);
  const axisOptions = useMemo(
    () =>
      buildAxisOptions({
        resolver: safeResolver,
        postFieldAxes: postFieldAxes.map((a) => ({ key: a.key, label: a.label })),
        customAxes: customAxes.map((c) => ({ key: c.key, label: c.label })),
        hiddenAxisKeys: matrixRules?.hiddenAxisKeys,
        perspectives,
        defaultPerspectiveLabel,
      }),
    [
      safeResolver,
      postFieldAxes,
      customAxes,
      matrixRules?.hiddenAxisKeys,
      perspectives,
      defaultPerspectiveLabel,
    ],
  );
  const axisDeps = useMemo(
    () => ({
      resolver: safeResolver,
      outputFormat,
      postFieldAxisByKey,
      customRuleByKey,
      includeEmptyKeys,
      perspectives,
    }),
    [
      safeResolver,
      outputFormat,
      postFieldAxisByKey,
      customRuleByKey,
      includeEmptyKeys,
      perspectives,
    ],
  );
  const filterGroupsByKey = useMemo(() => {
    const m = new Map<string, AxisGroup[]>();
    for (const opt of axisOptions) {
      m.set(
        opt.key,
        buildAxisGroups(opt.key, {
          resolver: safeResolver,
          posts: sourcePosts,
          isVertical: false,
          postFieldAxisByKey,
          customRuleByKey,
          includeEmptyKeys,
          perspectives,
        }),
      );
    }
    return m;
  }, [
    perspectives,
    axisOptions,
    safeResolver,
    sourcePosts,
    postFieldAxisByKey,
    customRuleByKey,
    includeEmptyKeys,
  ]);

  const [filterSelections, setFilterSelections] = useState<Record<string, Set<string>>>({});

  // 共通の絞込ロジック (posts を選択 bucket の AND 合成で絞る)。
  const filterPosts = (posts: PostMinimal[], selections: Record<string, Set<string>>) => {
    const activeKeys = Object.keys(selections).filter((k) => (selections[k]?.size ?? 0) > 0);
    if (activeKeys.length === 0) return posts;
    return posts.filter((p) => {
      for (const k of activeKeys) {
        const sel = selections[k]!;
        const groups = filterGroupsByKey.get(k) ?? [];
        const codes = postCodesForAxis(p, k, groups, axisDeps);
        let ok = false;
        for (const c of codes) {
          if (sel.has(c)) {
            ok = true;
            break;
          }
        }
        if (!ok) return false;
      }
      return true;
    });
  };
  // sourcePosts に現在のフィルタを適用した安定参照 (再描画コスト削減のため memo 化)。
  const filteredPosts = useMemo(
    () => filterPosts(sourcePosts, filterSelections),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sourcePosts, filterSelections, filterGroupsByKey, axisDeps],
  );

  const toggleFilterValue = (axisKey: string, code: string) => {
    setFilterSelections((prev) => {
      const next = { ...prev };
      const set = new Set(next[axisKey] ?? []);
      if (set.has(code)) set.delete(code);
      else set.add(code);
      next[axisKey] = set;
      return next;
    });
  };
  const clearAllFilters = () => setFilterSelections({});
  const activeFilterCount = Object.values(filterSelections).filter(
    (s) => (s?.size ?? 0) > 0,
  ).length;

  const applyFilter = (posts: PostMinimal[]): PostMinimal[] => filterPosts(posts, filterSelections);

  const buildFilterList = (): string[] => {
    const list: string[] = [];
    for (const opt of axisOptions) {
      const sel = filterSelections[opt.key];
      if (!sel || sel.size === 0) continue;
      const groups = filterGroupsByKey.get(opt.key) ?? [];
      const labels = [...sel].map((code) => groups.find((g) => g.code === code)?.label ?? code);
      list.push(`${opt.label}: ${labels.join(', ')}`);
    }
    return list;
  };

  const groupsForAxis = (axisKey: string): AxisGroup[] => filterGroupsByKey.get(axisKey) ?? [];
  const colorForPost = (post: PostMinimal, axisKey: string): string | null => {
    if (!axisKey) return null;
    const groups = filterGroupsByKey.get(axisKey) ?? [];
    if (groups.length === 0) return null;
    const codes = postCodesForAxis(post, axisKey, groups, axisDeps);
    for (const c of codes) {
      const g = groups.find((x) => x.code === c);
      if (g) return g.color;
    }
    return null;
  };

  return {
    axisOptions,
    filterGroupsByKey,
    filterSelections,
    toggleFilterValue,
    clearAllFilters,
    activeFilterCount,
    applyFilter,
    filteredPosts,
    buildFilterList,
    colorForPost,
    groupsForAxis,
  };
}

/**
 * 軸駆動フィルタの開閉トグル + bucket chip パネル (master/dataSettings/M5 由来、色は AxisGroup.color)。
 * `compact` は狭いサイドパネル用にラベル幅を詰める。
 */
export function AxisFilterPanel({
  filter,
  open,
  onToggleOpen,
  compact = false,
}: {
  filter: AxisFilterResult;
  open: boolean;
  onToggleOpen: () => void;
  compact?: boolean;
}) {
  const {
    axisOptions,
    filterGroupsByKey,
    filterSelections,
    toggleFilterValue,
    clearAllFilters,
    activeFilterCount,
  } = filter;
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={onToggleOpen}
          aria-expanded={open}
          className={`text-xs px-2.5 py-1 rounded font-semibold min-h-touch ${
            open || activeFilterCount > 0
              ? 'bg-primary-600 text-white'
              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
          }`}
        >
          {open ? '▲ フィルタを閉じる' : '▼ フィルタ'}
          {activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
        </button>
        {activeFilterCount > 0 && (
          <button
            type="button"
            onClick={clearAllFilters}
            className="text-[11px] text-slate-500 hover:text-slate-700 underline"
          >
            フィルタ解除
          </button>
        )}
      </div>
      {open && (
        <div className="flex flex-col gap-2">
          {axisOptions.length === 0 && (
            <p className="text-xs text-slate-400">絞込に使える軸がありません。</p>
          )}
          {axisOptions.map((opt) => {
            const groups = filterGroupsByKey.get(opt.key) ?? [];
            if (groups.length === 0) return null;
            const sel = filterSelections[opt.key] ?? new Set<string>();
            return (
              <div key={opt.key} className="flex flex-wrap items-center gap-1.5">
                <span
                  className={`text-xs font-semibold text-slate-600 shrink-0 ${
                    compact ? '' : 'min-w-[5rem]'
                  }`}
                >
                  {opt.label}
                </span>
                <div className="flex flex-wrap gap-1">
                  {groups.map((g) => {
                    const checked = sel.has(g.code);
                    return (
                      <button
                        key={g.code}
                        type="button"
                        onClick={() => toggleFilterValue(opt.key, g.code)}
                        aria-pressed={checked}
                        className="px-2 py-0.5 rounded-full text-[11px] min-h-touch border"
                        style={
                          checked
                            ? {
                                backgroundColor: g.color,
                                color: contrastTextColor(g.color),
                                borderColor: g.color,
                              }
                            : {
                                backgroundColor: '#f1f5f9',
                                color: '#334155',
                                borderColor: '#e2e8f0',
                              }
                        }
                      >
                        {g.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
